import { makeAutoObservable, runInAction } from "mobx";
import { ANALYTICS_EVENTS } from "../../../../analytics-store.tsx";
import { POSTHOG_EVENTS } from "../../../../constants/PostHogEvents";
import { CrmEventTargets, CrmOrgEvents, CrmUserEvents } from "../../../../constants/crm.ts";
import { fileDownload } from "../../../../helpers/files.ts";
import { getSelectedOrganization } from "../../../../helpers/localStorage.ts";
import { OrgFeatures } from "../../../../models/billing.ts";
import { PaginatedItems } from "../../../../models/common.ts";
import postHogService from "../../../../services/PostHogService";
import QueryService from "../../../../utils/QueryService";
import iframeStore from "../../../IframeNew/iframe-store.tsx";
import { ITgStore } from "../../store/new-tg-stores";

export interface ITgModelStore {
	modelsList: TgModel[];
	modelForm: TgModel;
	formErrors: { save?: boolean };
	isDatasetFromExisting: boolean;
	modelToView: TgModel;

	loadingModel: boolean;
	loadingModelsList: boolean;
	modelsListError: boolean;
	loadingCreateModel: boolean;
	loadingUpdateModel: boolean;
	loadingDeleteModel: boolean;
	loadingStartTraining: boolean;
	loadingStopTraining: boolean;
	downloadingModel: boolean;

	handleFormChange: <K extends keyof TgModel>(key: K, value: TgModel[K]) => void;
	getModel: (id: number) => Promise<TgModel>;
	createModel: (modelToCreate: TgModel) => Promise<TgModel>;
	updateModel: (id: number, updatedModel: TgModel) => Promise<TgModel>;
	deleteModel: (id: number) => Promise<void>;

	startTraining: (newModel: TgModel) => Promise<void>;
	stopTraining: (id: number) => Promise<void>;
	restartTraining: (oldModel: TgModel) => Promise<void>;
	listAllModels: (status?: TgModelStatus, includeTrainingParameters?: boolean) => Promise<TgModel[]>;
	downloadModel: (id: number) => Promise<void>;
	getBaseModel: (id: number) => Promise<string>;
	getModelCheckpoints: (modelId: number) => Promise<any[]>;
	defaultExpertParameters?: TgModelParameters;
	fetchDefaultExpertParameters: (args: { ip_medium: string; ip_type: string; dataset_size: number }) => Promise<void>;

	reset: () => void;
}

export class TgModelStore implements ITgModelStore {
	private queryService: QueryService = new QueryService("/new-tailored-generation/models");
	tgStore: ITgStore;

	modelForm: TgModel = defaultModel;
	modelsList: TgModel[] = [];
	formErrors: { save?: boolean; invalidName?: boolean } = {};
	isDatasetFromExisting: boolean = false;
	modelToView: TgModel = defaultModel;

	loadingProjectModels: boolean = false;
	loadingModel: boolean = false;
	loadingModelsList: boolean = false;
	loadingCreateModel: boolean = false;
	loadingUpdateModel: boolean = false;
	loadingDeleteModel: boolean = false;
	loadingStartTraining: boolean = false;
	loadingStopTraining: boolean = false;
	modelsListError: boolean = false;
	downloadingModel: boolean = false;
	defaultExpertParameters?: TgModelParameters;

	constructor(tgStore: ITgStore) {
		makeAutoObservable(this);
		this.tgStore = tgStore;
	}

	handleFormChange = <K extends keyof TgModel>(key: K, value: TgModel[K]) => {
		this.modelForm = { ...this.modelForm, [key]: value };
	};

	getModel = async (id: number) => {
		try {
			this.loadingModel = true;
			const model = await this.queryService.get(`/${id}`);

			runInAction(() => {
				this.loadingModel = false;
			});

			return model;
		} catch (err: any) {
			this.loadingModel = false;
			return Promise.reject(err);
		}
	};

	createModel = async (modelToCreate: TgModel): Promise<TgModel> => {
		try {
			this.loadingCreateModel = true;
			const newModel = await this.queryService.post(`/`, modelToCreate);
			runInAction(() => {
				this.loadingCreateModel = false;
			});

			return newModel;
		} catch (err: any) {
			this.loadingCreateModel = false;
			return Promise.reject(err);
		}
	};

	updateModel = async (id: number, modelToUpdate: TgModel): Promise<TgModel> => {
		try {
			this.loadingUpdateModel = true;
			const updatedModel = await this.queryService.put(`/${id}`, modelToUpdate);

			runInAction(() => {
				this.tgStore.tgProjectStore.paginatedModels.items =
					this.tgStore.tgProjectStore.paginatedModels?.items.map((model) =>
						model.id === id ? updatedModel : model,
					);
				this.loadingUpdateModel = false;
			});

			return updatedModel;
		} catch (err: any) {
			this.loadingUpdateModel = false;
			return Promise.reject(err);
		}
	};

	deleteModel = async (id: number) => {
		try {
			this.loadingDeleteModel = true;
			await this.queryService.delete(`/${id}`);

			runInAction(() => {
				this.tgStore.tgProjectStore.paginatedModels = {
					items: this.tgStore.tgProjectStore.paginatedModels.items.filter((model) => model.id !== id),
					total: this.tgStore.tgProjectStore.paginatedModels.total - 1,
				};
				this.loadingDeleteModel = false;
			});
		} catch (err: any) {
			this.loadingDeleteModel = false;
			return Promise.reject(err);
		}
	};

	startTraining = async (tgModel: TgModel): Promise<void> => {
		const startTime = Date.now();
		
		try {
			this.loadingStartTraining = true;
			const trainingParams = tgModel?.training_parameters ?? this.modelForm?.training_parameters;
			const body = trainingParams ? { ...trainingParams } : {};
			
			// Get dataset size for tracking
			const dataset = this.tgStore.tgDatasetStore.datasetForm;
			const datasetSize = dataset?.images?.length || 0;
			
			await this.queryService.post(`/${tgModel.id}/start_training`, body);
			
			if (tgModel) {
				const project = this.tgStore.tgProjectStore.projectToView;
				const properties = {
					project_id: tgModel.project_id.toString(),
					model_id: tgModel.id.toString(),
					model_name: tgModel.name,
					project_name: project.project_name,
				};
				this.tgStore.rootStore.appStore.logCrmEvents([
					{
						eventTarget: CrmEventTargets.ORG,
						eventType: CrmOrgEvents.ORG_USER_STARTED_TG_MODEL_TRAINING,
						properties: properties,
					},
					{
						eventTarget: CrmEventTargets.USER,
						eventType: CrmUserEvents.USER_STARTED_TG_MODEL_TRAINING,
						properties: properties,
					},
				]);
				
							// Track training started
			postHogService.trackEvent(POSTHOG_EVENTS.TG_MODEL_TRAINING_STARTED, {
				project_id: tgModel.project_id.toString(),
				dataset_size: datasetSize,
			}, this.tgStore.rootStore.authStore.user, { send_instantly: true });
				
				// Store start time for completion tracking
				this.storeTrainingStartTime(tgModel.id, startTime);
			}

			runInAction(() => {
				this.tgStore.tgProjectStore.paginatedModels.items =
					this.tgStore.tgProjectStore.paginatedModels.items.map((model) =>
						model.id === tgModel.id ? { ...model, status: "InProgress" } : model,
					);

				this.loadingStartTraining = false;
			});
		} catch (err: any) {
			this.loadingStartTraining = false;
			
					// Track training start failed
		postHogService.trackEvent(POSTHOG_EVENTS.TG_MODEL_TRAINING_FAILED, {
			error: err?.message || err?.toString() || 'Unknown error',
		}, this.tgStore.rootStore.authStore.user, { send_instantly: true });
			
			return Promise.reject(err);
		}
	};

	// Helper to store training start times
	private trainingStartTimes: Map<number, number> = new Map();
	
	private storeTrainingStartTime = (modelId: number, startTime: number) => {
		this.trainingStartTimes.set(modelId, startTime);
	};

	trackTrainingCompletion = (modelId: number) => {
		const startTime = this.trainingStartTimes.get(modelId);
		if (startTime) {
			const trainingDuration = Date.now() - startTime;
			
			postHogService.trackEvent(POSTHOG_EVENTS.TG_MODEL_TRAINING_COMPLETED, {
				project_id: modelId.toString(),
				training_duration: trainingDuration,
			}, this.tgStore.rootStore.authStore.user, { send_instantly: true });
			
			// Clean up stored start time
			this.trainingStartTimes.delete(modelId);
		}
	};

	// Check for status changes and track completion
	checkModelStatusChanges = (newModels: TgModel[]) => {
		const currentModels = this.tgStore.tgProjectStore.paginatedModels.items;
		
		newModels.forEach(newModel => {
			const oldModel = currentModels.find(m => m.id === newModel.id);
			
			// If model changed from InProgress to Completed, track completion
			if (oldModel?.status === "InProgress" && newModel.status === "Completed") {
				this.trackTrainingCompletion(newModel.id);
			}
			
			// If model failed, track failure
			if (oldModel?.status === "InProgress" && newModel.status === "Failed") {
				postHogService.trackEvent(POSTHOG_EVENTS.TG_MODEL_TRAINING_FAILED, {
					project_id: newModel.project_id.toString(),
					error: "Training failed",
				}, this.tgStore.rootStore.authStore.user, { send_instantly: true });
				
				// Clean up stored start time
				this.trainingStartTimes.delete(newModel.id);
			}
		});
	};

	stopTraining = async (id: number): Promise<void> => {
		try {
			this.loadingStopTraining = true;
			await this.queryService.post(`/${id}/stop_training`);

			runInAction(() => {
				this.tgStore.tgProjectStore.paginatedModels.items =
					this.tgStore.tgProjectStore.paginatedModels.items.map((model) =>
						model.id === id ? { ...model, status: "Stopping" } : model,
					);
				this.loadingStopTraining = false;
			});
		} catch (err: any) {
			this.loadingStopTraining = false;
			return Promise.reject(err);
		}
	};

	restartTraining = async (oldModel: TgModel): Promise<void> => {
		try {
			// Save original index for in-place replacement
			const { paginatedModels } = this.tgStore.tgProjectStore;
			const originalIndex = paginatedModels.items.findIndex((m) => m.id === oldModel.id);

			// 2) Create a new model based on the old model's data
			const modelToCreate = { ...oldModel } as TgModel;
			const createdModel = await this.createModel(modelToCreate);

			// 3) Replace old model row in-place with new model
			runInAction(() => {
				this.tgStore.tgProjectStore.paginatedModels.items[originalIndex] = createdModel;
			});

			// 4) Start training on the newly created model
			await this.startTraining(createdModel);

			// 5) Delete the old model
			await this.queryService.delete(`/${oldModel.id}`);
		} catch (err: any) {
			return Promise.reject(err);
		}
	};

	listAllModels = async (status?: TgModelStatus, includeTrainingParameters: boolean = false): Promise<TgModel[]> => {
		try {
			this.loadingModelsList = true;
			this.modelsListError = false;

			let filteredModels: TgModel[] = [];
			if (getSelectedOrganization()?.organization.uid) {
				if (
					!iframeStore.isIframe() ||
					this.tgStore.rootStore.authStore.isFeatureEnabled(OrgFeatures.TAILORED_GENERATION)
				) {
					const iframeTgModels = iframeStore.iframe?.config.image_generation_config?.enabled_tg_models ?? [];
					if (!iframeStore.isIframe() || iframeTgModels.length) {
						const resp: PaginatedItems<TgModel> = await this.queryService.get(`/`, {
							params: status ? { status, include_training_parameters: includeTrainingParameters } : {},
						});
						filteredModels = resp.items;
						if (iframeStore.isIframe()) {
							filteredModels = filteredModels.filter((model: TgModel) =>
								iframeTgModels.includes(model.id.toString()),
							);
						}
					}
				}
			}

					runInAction(() => {
			// Check for status changes before updating the list
			this.checkModelStatusChanges(filteredModels);
			
			this.modelsList = filteredModels;
			this.loadingModelsList = false;
		});

		return this.modelsList;
		} catch (err: any) {
			this.loadingModelsList = false;
			this.modelsListError = true;
			return Promise.reject(err);
		}
	};

	downloadModel = async (id: number): Promise<void> => {
		try {
			runInAction(() => {
				this.downloadingModel = true;
			});

			const response = await this.queryService.get(`/${id}/download`);
			fileDownload(response.download_url, response.base_model_name);
			this.tgStore.rootStore.appStore.logCrmEvents([
				{
					eventTarget: CrmEventTargets.ORG,
					eventType: CrmOrgEvents.ORG_DOWNLOADED_TG_MODEL_SOURCE,
				},
				{
					eventTarget: CrmEventTargets.USER,
					eventType: CrmUserEvents.USER_DOWNLOADED_TG_MODEL_SOURCE,
				},
			]);
			this.tgStore.rootStore.analyticsStore.logEvent(ANALYTICS_EVENTS.DOWNLOAD_TG_MODEL_SOURCE);
		} catch (err: any) {
			this.downloadingModel = false;
			return Promise.reject(err);
		} finally {
			runInAction(() => {
				this.downloadingModel = false;
			});
		}
	};

	getBaseModel = async (id: number): Promise<string> => {
		try {
			const response = await this.queryService.get(`/${id}/download`);
			return response.base_model_card;
		} catch (err: any) {
			return Promise.reject(err);
		}
	};

	getModelCheckpoints = async (modelId: number): Promise<any[]> => {
		try {
			const response = await this.queryService.get(`/${modelId}/checkpoints`);
			return response.checkpoints || [];
		} catch (err: any) {
			return Promise.reject(err);
		}
	};

	fetchDefaultExpertParameters = async ({
		ip_medium,
		ip_type,
		dataset_size,
	}: {
		ip_medium: string;
		ip_type: string;
		dataset_size: number;
	}) => {
		const response = await this.queryService.post(`/fetch_default_parameters`, {
			ip_medium,
			ip_type,
			dataset_size,
		});
		runInAction(() => {
			this.defaultExpertParameters = response as TgModelParameters;
		});
	};

	reset = () => {
		this.modelsList = [];
		this.modelForm = defaultModel;
		this.formErrors = {};
		this.isDatasetFromExisting = false;
		this.modelToView = defaultModel;
		this.loadingModel = false;
		this.loadingModelsList = false;
		this.modelsListError = false;
		this.loadingCreateModel = false;
		this.loadingUpdateModel = false;
		this.loadingDeleteModel = false;
		this.loadingStartTraining = false;
		this.loadingStopTraining = false;
		this.downloadingModel = false;
	};
}

export type TgModelParameters = {
	rank: number;
	checkpoint_interval?: number;
	lr_scheduler: string;
	learning_rate: number;
	total_training_steps: number;
};

export type TgModel = {
	id: number;
	name: string;
	description: string;
	training_version: TgTrainingVersion;
	generation_prefix: string;
	status: TgModelStatus;
	project_id: number;
	dataset_id: number;
	created_at: string;
	updated_at: string;
	custom_metadata: string;
	training_mode?: TrainingMode;
	training_parameters?: TgModelParameters;
	checkpoint_steps?: string[];
	active_model_version?: string;
};

export type TgTrainingVersion = "light" | "bria-2.3" | "max" | "bria-3.2";
export type TgModelStatus = "Created" | "InProgress" | "Completed" | "Failed" | "Stopping" | "Stopped" | "Deleted";

export enum Rank {
	RANK_4 = 4,
	RANK_8 = 8,
	RANK_16 = 16,
	RANK_32 = 32,
	RANK_64 = 64,
	RANK_128 = 128,
	RANK_256 = 256,
}

export enum TgModelLearningRateScheduler {
	LINEAR = "linear",
	COSINE = "cosine",
	COSINE_WITH_RESTARTS = "cosine_with_restarts",
	POLYNOMIAL = "polynomial",
	CONSTANT = "constant",
	CONSTANT_WITH_WARMUP = "constant_with_warmup",
}

export enum TrainingMode {
	FULLY_AUTOMATED = "fully_automated",
	EXPERT = "expert",
}

export enum CreateTgModelPanels {
	TRAINING_MODE_PANEL = "TRAINING_MODE_PANEL",
	TRAINING_VERSION_PANEL = "TRAINING_VERSION_PANEL",
	SELECT_DATASET_PANEL = "SELECT_DATASET_PANEL",
	UPLOAD_METHOD_PANEL = "UPLOAD_METHOD_PANEL",
	ADVANCED_UPLOAD_PANEL = "ADVANCED_UPLOAD_PANEL",
	ADVANCED_UPLOAD_LOADING_PANEL = "ADVANCED_UPLOAD_LOADING_PANEL",
	ADVANCED_UPLOAD_COMPLETED_PANEL = "ADVANCED_UPLOAD_COMPLETED_PANEL",
	ADVANCED_UPLOAD_ERROR_PANEL = "ADVANCED_UPLOAD_ERROR_PANEL",
	PARAMETERS_SETUP_PANEL = "PARAMETERS_SETUP_PANEL",
	CREATE_MODEL_DATASET_PANEL = "CREATE_MODEL_DATASET_PANEL",
}

// Learning rate config: eight discrete values
export const LEARNING_RATE_VALUES: number[] = [
	1e-6, 2e-6, 3e-6, 4e-6, 5e-6, 6e-6, 7e-6, 8e-6, 9e-6, 1e-5, 2e-5, 3e-5, 4e-5, 5e-5, 6e-5, 7e-5, 8e-5, 9e-5, 1e-4,
	2e-4, 3e-4, 4e-4, 5e-4, 6e-4, 7e-4, 8e-4, 9e-4, 1e-3,
];
export const LEARNING_RATE_CONFIG = {
	min: 1e-6,
	max: 1e-3,
};

// Total training steps config
export const TOTAL_TRAINING_STEPS_CONFIG = {
	min: 100,
	max: 5000,
	step: 100, // Every 100
};

export enum CheckpointInterval {
	INTERVAL_100 = 100,
	INTERVAL_200 = 200,
	INTERVAL_500 = 500,
	INTERVAL_1000 = 1000,
	INTERVAL_1500 = 1500,
}

export const defaultModel: TgModel = {
	id: NaN,
	name: "",
	description: "",
	custom_metadata: "",
} as TgModel;
